import { useMemo } from 'react';

import { getFullName } from '@klareospace/pharos-lib-utils';
import { USER_STATUS } from '@klareospace/pharos-lib-types';
import type { SortParams, UserWithResolvedScopes } from '@klareospace/pharos-lib-types';
import type { MultiSelectOption } from '@klareospace/pharos-lib-web-utils';

import type { DriverSortableColumn } from '../types/driver.types';
import { useDrivers } from './use-drivers';

// ─── Constants ────────────────────────────────────────────────────────────────

const DRIVER_FILTER_PAGE_SIZE = 30;

// Deterministic order — otherwise "the first N" would be arbitrary Mongo order.
const DRIVER_SELECT_SORT: SortParams<DriverSortableColumn> = { sortBy: 'firstName', sortDirection: 'asc' };

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseDriversForSelectOptions {
  carrierIds?: string[];
  /** Excludes the network call entirely when false. */
  enabled?: boolean;
  siteIds?: string[];
  spaceId?: string;
  /** Raw (already-debounced) search text; forwarded as-is to `POST /drivers/search`. */
  text?: string;
}

export interface UseDriversForSelectReturn {
  /** Active drivers, mapped to select options. */
  activeOptions: MultiSelectOption[];
  /** How many matching drivers exist beyond the fetched page (0 when all matches were returned). */
  hiddenCount: number;
  /** Inactive drivers, mapped to select options. Pending drivers are excluded by design (never requested). */
  inactiveOptions: MultiSelectOption[];
  /** True while the underlying drivers query is in-flight. */
  isLoading: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Falls back to the driver's email when both names are blank, so a row/badge is never unlabeled. */
export function toDriverOption(driver: UserWithResolvedScopes): MultiSelectOption {
  return { label: getFullName(driver.firstName, driver.lastName) || driver.email, value: driver.id };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetches active + inactive drivers (capped at `DRIVER_FILTER_PAGE_SIZE`) and partitions them by
 * `status` for a sectioned Driver `MultiSelect`; `hiddenCount` powers a "search for more" hint.
 */
export function useDriversForSelect(options?: UseDriversForSelectOptions): UseDriversForSelectReturn {
  const { carrierIds, enabled = true, siteIds, spaceId, text } = options ?? {};

  const { data, isFetching } = useDrivers({
    carrierIds,
    enabled,
    page: 1,
    pageSize: DRIVER_FILTER_PAGE_SIZE,
    siteIds,
    sort: DRIVER_SELECT_SORT,
    spaceId,
    statuses: [USER_STATUS.ACTIVE, USER_STATUS.INACTIVE],
    text,
  });

  const activeOptions = useMemo(
    () => (data?.items ?? []).filter((driver) => driver.status === USER_STATUS.ACTIVE).map(toDriverOption),
    [data],
  );

  const inactiveOptions = useMemo(
    () => (data?.items ?? []).filter((driver) => driver.status === USER_STATUS.INACTIVE).map(toDriverOption),
    [data],
  );

  const hiddenCount = useMemo(() => {
    const total = data?.total ?? 0;
    const fetchedCount = data?.items.length ?? 0;
    return total > fetchedCount ? total - fetchedCount : 0;
  }, [data]);

  return { activeOptions, hiddenCount, inactiveOptions, isLoading: isFetching };
}
